import { useEffect, useState } from "react";
import { MapContainer, Marker, TileLayer, useMapEvents } from "react-leaflet";
import {
  CheckCircle2,
  LoaderCircle,
  LocateFixed,
  MapPinned,
  Send,
  XCircle,
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

import api from "../interceptors.js";
import "./index.css";

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition({
        lat: e.latlng.lat.toFixed(6),
        lng: e.latlng.lng.toFixed(6),
      });
    },
  });

  useEffect(() => {
    if (position.lat && position.lng) {
      map.setView([position.lat, position.lng], map.getZoom(), {
        animate: true,
      });
    }
  }, [position.lat, position.lng, map]);

  if (!position.lat || !position.lng) return null;

  const icon = new L.Icon({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  return (
    <Marker
      position={[position.lat, position.lng]}
      icon={icon}
      draggable
      eventHandlers={{
        dragend: (e) => {
          const { lat, lng } = e.target.getLatLng();
          setPosition({
            lat: lat.toFixed(6),
            lng: lng.toFixed(6),
          });
        },
      }}
    />
  );
}

export default function DeviceSetup() {
  const [position, setPosition] = useState({ lat: "", lng: "" });
  const [loading, setLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cpoFound, setCpoFound] = useState(false);

  const [popup, setPopup] = useState({
    show: false,
    message: "",
    type: "",
  });

  const [formData, setFormData] = useState({
    station_id: "",
    station_name: "",
    customer_id: "",
    customer_name: "",
    address: "",
    technician: "",
    contact_number: "",
    pincode: "",
    state: "",
    state_district: "",
    country: "",
  });

  useEffect(() => {
    fetch("https://restcountries.com/v3.1/all?fields=name,cca2")
      .then((res) => res.json())
      .then((data) => {
        const countryList = data
          .map((country) => ({
            name: country.name.common,
            code: country.cca2,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setCountries(countryList);
      })
      .catch((err) => console.error("Country fetch error:", err));
  }, []);

  const handleChange = async (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({ ...prev, [id]: value }));

    if (id === "customer_id") {
      if (!value) {
        setCpoFound(false);
        return;
      }

      try {
        const res = await api.get(`/api/cpo/search/${value}`);
        const results = res.data?.results || [];
        const match = results.find((cpo) => cpo.customer_id === value);

        if (match) {
          setFormData((prev) => ({
            ...prev,
            customer_name: match.name,
          }));
          setCpoFound(true);
        } else {
          setCpoFound(false);
        }
      } catch (err) {
        console.error("CPO search failed", err);
      }
    }
  };

  const API_KEY = "efb6050a1082404aa16c97fcee361403";

  const getAddressFromPincode = async (pincode, countryCode) => {
    try {
      setPincodeLoading(true);

      const res = await fetch(
        `https://api.opencagedata.com/geocode/v1/json?key=${API_KEY}&q=${pincode}&countrycode=${countryCode}&pretty=1&no_annotations=1`,
      );

      const data = await res.json();

      if (!data.results || data.results.length === 0) return;

      const comp = data.results[0].components;

      const address = [comp.city_district, comp.county, comp.state_district]
        .filter(Boolean)
        .join(", ");

      setFormData((prev) => ({
        ...prev,
        address,
        state: comp.state || "",
        state_district: comp.state_district || "",
      }));
    } catch (err) {
      console.error("Address fetch error", err);
    } finally {
      setPincodeLoading(false);
    }
  };

  const maybeResolvePincode = (pincodeValue, countryName) => {
    const countryCode = countries.find((country) => country.name === countryName)
      ?.code;

    if (pincodeValue.trim().length >= 4 && countryCode) {
      getAddressFromPincode(pincodeValue.trim(), countryCode);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!cpoFound && !formData.customer_name) {
      setPopup({
        show: true,
        message: "Customer name is required for a new CPO.",
        type: "error",
      });
      return;
    }

    if (!position.lat || !position.lng) {
      setPopup({
        show: true,
        message: "Select a station location on the map.",
        type: "error",
      });
      return;
    }

    setLoading(true);

    const trimmedData = Object.fromEntries(
      Object.entries(formData).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ]),
    );

    const payload = {
      ...trimmedData,
      lat: position.lat,
      lng: position.lng,
    };

    try {
      const res = await api.post("/api/post_station_details", payload);

      if (res.status === 201 || res.status === 200) {
        setPopup({
          show: true,
          message: "Station details posted successfully.",
          type: "success",
        });

        setFormData({
          station_id: "",
          station_name: "",
          customer_id: "",
          customer_name: "",
          address: "",
          technician: "",
          pincode: "",
          state: "",
          country: "",
          state_district: "",
          contact_number: "",
        });
        setPosition({ lat: "", lng: "" });
        setShowMap(false);
      } else {
        setPopup({
          show: true,
          message: res.data?.message || "Failed to post details.",
          type: "error",
        });
      }
    } catch (err) {
      setPopup({
        show: true,
        message: err.response?.data?.message || "Server error. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const formFields = [
    ["station_id", "Station ID"],
    ["station_name", "Station Name"],
    ["customer_id", "Customer ID"],
    ["customer_name", "Customer Name"],
    ["technician", "Responsible Technician"],
    ["contact_number", "Contact Number"],
  ];

  return (
    <div className="setup-workspace">
      <form onSubmit={handleSubmit} className="setup-form-panel">
        <div className="setup-panel-heading">
          <div>
            <p>Provision station</p>
            <h2>Station identity</h2>
          </div>
          <span>{cpoFound ? "Existing CPO" : "New station"}</span>
        </div>

        <div className="setup-form-grid">
          {formFields.map(([id, label]) => (
            <label key={id} className="setup-field">
              <span>{label}</span>
              <input
                id={id}
                disabled={loading || (id === "customer_name" && cpoFound)}
                value={formData[id]}
                onChange={handleChange}
              />
              {id === "customer_id" && cpoFound && (
                <small className="field-status is-success">
                  Existing CPO detected
                </small>
              )}
              {id === "customer_id" && !cpoFound && formData.customer_id && (
                <small className="field-status is-warning">
                  New CPO will be created
                </small>
              )}
            </label>
          ))}

          <label className="setup-field">
            <span>Latitude</span>
            <input
              type="number"
              step="any"
              value={position.lat}
              disabled={loading}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || (value >= -90 && value <= 90)) {
                  setPosition((p) => ({ ...p, lat: value }));
                }
              }}
              placeholder="Latitude (-90 to 90)"
            />
          </label>

          <label className="setup-field">
            <span>Longitude</span>
            <input
              type="number"
              step="any"
              value={position.lng}
              disabled={loading}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || (value >= -180 && value <= 180)) {
                  setPosition((p) => ({ ...p, lng: value }));
                }
              }}
              placeholder="Longitude (-180 to 180)"
            />
          </label>

          <label className="setup-field">
            <span>Pincode</span>
            <div className="field-with-loader">
              <input
                id="pincode"
                value={formData.pincode}
                onChange={handleChange}
                onBlur={(e) =>
                  maybeResolvePincode(e.target.value, formData.country)
                }
                placeholder="Enter pincode"
              />
              {pincodeLoading && <LoaderCircle size={17} className="spin-icon" />}
            </div>
          </label>

          <label className="setup-field">
            <span>Country</span>
            <select
              id="country"
              value={formData.country}
              onChange={(e) => {
                handleChange(e);
                maybeResolvePincode(formData.pincode, e.target.value);
              }}
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
          </label>

          <label className="setup-field">
            <span>State</span>
            <input
              id="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Enter state"
            />
          </label>

          <label className="setup-field">
            <span>District</span>
            <input
              id="state_district"
              value={formData.state_district}
              onChange={handleChange}
              placeholder="Enter district"
            />
          </label>

          <label className="setup-field setup-field-wide">
            <span>Address</span>
            <textarea
              id="address"
              disabled={loading}
              value={formData.address}
              onChange={handleChange}
              rows="3"
            />
          </label>
        </div>

        <button type="submit" disabled={loading} className="setup-submit-button">
          {loading ? (
            <>
              <LoaderCircle size={18} className="spin-icon" />
              Submitting
            </>
          ) : (
            <>
              <Send size={18} />
              Submit station
            </>
          )}
        </button>
      </form>

      <aside className={`setup-map-panel ${showMap ? "is-mobile-visible" : ""}`}>
        <div className="setup-map-header">
          <div>
            <p>Location</p>
            <h2>Map pin</h2>
          </div>
          <span>
            <LocateFixed size={16} />
            {position.lat && position.lng ? "Pinned" : "Not set"}
          </span>
        </div>

        <div className="setup-map-frame">
          <MapContainer
            center={[20.5937, 78.9629]}
            zoom={4}
            className="setup-map"
          >
            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationMarker position={position} setPosition={setPosition} />
          </MapContainer>
        </div>
      </aside>

      <button
        type="button"
        onClick={() => setShowMap((value) => !value)}
        className="setup-map-toggle"
      >
        <MapPinned size={18} />
        {showMap ? "Hide Map" : "Pick Location"}
      </button>

      {popup.show && (
        <div className="setup-popup-backdrop">
          <div className="setup-popup">
            <span className={popup.type === "success" ? "is-success" : "is-error"}>
              {popup.type === "success" ? (
                <CheckCircle2 size={28} />
              ) : (
                <XCircle size={28} />
              )}
            </span>
            <h2>{popup.type === "success" ? "Success" : "Action needed"}</h2>
            <p>{popup.message}</p>
            <button
              type="button"
              onClick={() => setPopup({ show: false, message: "", type: "" })}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
